import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EnhancedTable from '../../enhancedTable/EnhancedTable';
import SimpleDatePicker from '../../ui/simpleDatePicker';
import { AuditService } from '../../../service/auditService';

const AuditLogViewer = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [filters, setFilters] = useState({
        entityType: '',
        action: '',
        startDate: null,
        endDate: null,
        searchTerm: ''
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Fetch audit logs
    const { data, isLoading } = useQuery(
        ['auditLogs', page, pageSize, filters],
        () => AuditService.getAuditLogs({
            page: page + 1,
            pageSize,
            ...filters
        }),
        { keepPreviousData: true }
    );

    const columns = [
        {
            Header: 'Date/Time',
            accessor: 'createdAt',
            Cell: ({ value }) => new Date(value).toLocaleString()
        },
        {
            Header: 'User',
            accessor: row => `${row.userName || 'System'} (${row.userEmail || 'N/A'})`,
            id: 'user'
        },
        {
            Header: 'Action',
            accessor: 'action',
            id: 'auditAction',
            Cell: ({ value }) => (
                <Chip 
                    label={value} 
                    size="small"
                    color={
                        value === 'CREATE' ? 'success' :
                        value === 'UPDATE' ? 'info' :
                        value === 'DELETE' ? 'error' :
                        value === 'VIEW' ? 'default' :
                        'warning'
                    }
                />
            )
        },
        {
            Header: 'Entity Type',
            accessor: 'entityType'
        },
        {
            Header: 'Entity',
            accessor: 'entityName'
        },
        {
            Header: 'Description',
            accessor: 'description',
            Cell: ({ value }) => (
                <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {value}
                </Typography>
            )
        },
        {
            Header: 'Location',
            accessor: 'location.name',
            Cell: ({ value }) => value || 'N/A'
        },
        {
            Header: 'IP Address',
            accessor: 'ipAddress'
        },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <Tooltip title="View Details">
                    <IconButton 
                        size="small"
                        onClick={() => {
                            setSelectedLog(row.original);
                            setDetailsOpen(true);
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        } 
    ];

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(0); // Reset to first page
    };

    const tableData = data?.data?.rows || [];
    const totalCount = data?.data?.count || 0;

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                    Audit Log Viewer
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Entity Type</InputLabel>
                            <Select
                                value={filters.entityType}
                                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                                label="Entity Type"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Invoice">Invoice</MenuItem>
                                <MenuItem value="Customer">Customer</MenuItem>
                                <MenuItem value="Prescription">Prescription</MenuItem>
                                <MenuItem value="Appointment">Appointment</MenuItem>
                                <MenuItem value="Payment">Payment</MenuItem>
                                <MenuItem value="Inventory">Inventory</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={filters.action}
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                                label="Action"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="CREATE">Create</MenuItem>
                                <MenuItem value="UPDATE">Update</MenuItem>
                                <MenuItem value="DELETE">Delete</MenuItem>
                                <MenuItem value="VIEW">View</MenuItem>
                                <MenuItem value="PRINT">Print</MenuItem>
                                <MenuItem value="EXPORT">Export</MenuItem>
                                <MenuItem value="TRANSFER">Transfer</MenuItem>
                                <MenuItem value="VOID">Void</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <SimpleDatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            size="small"
                            margin="none"
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <SimpleDatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            size="small"
                            margin="none"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search (user, entity name, description)"
                            value={filters.searchTerm}
                            onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <EnhancedTable
                columns={columns}
                data={tableData}
                totalRowCount={totalCount}
                pageCount={Math.ceil(totalCount / pageSize)}
                fetchData={({ pageSize, pageIndex }) => {
                    setPage(pageIndex);
                    setPageSize(pageSize);
                }}
                loading={isLoading}
                selectionMode="none"
                showStatusColumn={false}
            />

            {/* Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Audit Log Details</DialogTitle>
                <DialogContent dividers>
                    {selectedLog && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">Action</Typography>
                                    <Typography>{selectedLog.action}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">Date/Time</Typography>
                                    <Typography>{new Date(selectedLog.createdAt).toLocaleString()}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">User</Typography>
                                    <Typography>{selectedLog.userName || 'System'}</Typography>
                                    <Typography variant="caption">{selectedLog.userEmail}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2">IP Address</Typography>
                                    <Typography>{selectedLog.ipAddress || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">Description</Typography>
                                    <Typography>{selectedLog.description}</Typography>
                                </Grid>
                                {selectedLog.oldValues && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Old Values</Typography>
                                        <pre style={{ backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto' }}>
                                            {JSON.stringify(selectedLog.oldValues, null, 2)}
                                        </pre>
                                    </Grid>
                                )}
                                {selectedLog.newValues && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">New Values</Typography>
                                        <pre style={{ backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto' }}>
                                            {JSON.stringify(selectedLog.newValues, null, 2)}
                                        </pre>
                                    </Grid>
                                )}
                                {selectedLog.metadata && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Metadata</Typography>
                                        <pre style={{ backgroundColor: '#f5f5f5', padding: 8, borderRadius: 4, overflow: 'auto' }}>
                                            {JSON.stringify(selectedLog.metadata, null, 2)}
                                        </pre>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AuditLogViewer;
